type Beat = {
  el: HTMLElement;
  from: number;
  to: number;
  parts: HTMLElement[];
  lines: HTMLElement[];
  chips: HTMLElement[];
};

type Chapter = {
  el: HTMLElement;
  beats: Beat[];
  stage: HTMLElement;
  src: string;
  dur: number;
  rate: number;
  cutEnd: number;
  video: HTMLVideoElement | null;
  playing: boolean;
  held: boolean;
  wait: boolean;
  armed: boolean;
  clock: number;
};

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
const ease = (x: number) => 1 - Math.pow(1 - x, 3);

export function initFilm(root: Document | HTMLElement = document): void {
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FINE = matchMedia('(pointer: fine)').matches;

  const FADE = 0.1;
  const STAGGER = 0.035;
  const NEAR = '200px';
  const FAR = '260%';

  const chapterEls = Array.from(root.querySelectorAll<HTMLElement>('.chapter'));
  if (!chapterEls.length) return;

  if (REDUCED) {
    chapterEls.forEach((el) => el.classList.add('is-static'));
    return;
  }

  chapterEls.forEach((el) => {
    el.querySelectorAll<HTMLElement>('.beat :is(h1, h2)[data-split="lines"]').forEach((h) => {
      h.innerHTML = h.innerHTML
        .split(/<br\s*\/?>/i)
        .map((s) => s.trim())
        .filter(Boolean)
        .map((l) => `<span class="line"><span class="line-in">${l}</span></span>`)
        .join('');
    });
  });

  const chapters: Chapter[] = chapterEls.map((el) => {
    const beats: Beat[] = Array.from(el.querySelectorAll<HTMLElement>('.beat')).map((b) => ({
      el: b,
      from: parseFloat(b.dataset.from || '0') || 0,
      to: b.dataset.to ? parseFloat(b.dataset.to) : 1,
      parts: (Array.from(b.children) as HTMLElement[]).filter((c) => !c.classList.contains('chips')),
      lines: Array.from(b.querySelectorAll<HTMLElement>('.line-in')),
      chips: Array.from(b.querySelectorAll<HTMLElement>('.chips li')),
    }));
    return {
      el,
      beats,
      stage: el.querySelector('.stage') as HTMLElement,
      src: el.dataset.video || '',
      dur: parseFloat(el.dataset.duration || '') || beats.length * 5,
      rate: Math.min(2, Math.max(0.2, parseFloat(el.dataset.rate || '') || 1)),
      cutEnd: Math.min(1, Math.max(0.2, parseFloat(el.dataset.loopEnd || '') || 1)),
      video: null,
      playing: false,
      held: false,
      wait: !!el.dataset.video,
      armed: false,
      clock: 0,
    };
  });

  const afterLCP = new Promise<void>((res) => {
    const img = document.querySelector('.chapter .plate');
    if (!img || (img as HTMLImageElement).complete) return res();
    img.addEventListener('load', () => res(), { once: true });
    img.addEventListener('error', () => res(), { once: true });
    setTimeout(res, 800);
  });

  function posterOf(c: Chapter): string {
    const plate = c.stage.querySelector<HTMLImageElement>('.plate');
    if (plate && plate.getAttribute('src')) return plate.getAttribute('src') as string;
    const url = c.el.dataset.poster;
    if (!url) return '';
    const img = document.createElement('img');
    img.className = 'plate';
    img.src = url;
    img.alt = '';
    c.stage.insertBefore(img, c.stage.firstChild);
    return url;
  }

  function spanOf(c: Chapter, v: HTMLVideoElement): number {
    const total = isFinite(v.duration) && v.duration > 0 ? v.duration : c.dur;
    return Math.max(0.5, total * c.cutEnd);
  }

  function start(c: Chapter): void {
    const v = c.video;
    if (!v) return;
    v.playbackRate = c.rate;
    const p = v.play();
    if (!p || !p.then) {
      c.playing = true;
      return;
    }
    p.then(() => {
      c.playing = true;
      if (c.wait) {
        c.wait = false;
      } else if (isFinite(v.duration) && v.duration > 0 && Math.abs(c.clock * spanOf(c, v) - v.currentTime) > 1.5) {
        v.currentTime = c.clock * spanOf(c, v);
      }
      if (c.held) v.pause();
    }).catch(() => {
      c.playing = false;
      c.wait = false;
      if (c.armed) return;
      c.armed = true;
      const retry = () => start(c);
      addEventListener('pointerdown', retry, { once: true, passive: true });
      addEventListener('touchstart', retry, { once: true, passive: true });
    });
  }

  async function attach(c: Chapter | undefined): Promise<void> {
    if (!c || c.video || !c.src) return;
    await afterLCP;
    if (c.video || !c.el.isConnected) return;
    const v = document.createElement('video');
    v.muted = true;
    v.playsInline = true;
    v.loop = true;
    v.playbackRate = c.rate;
    v.defaultPlaybackRate = c.rate;
    v.preload = 'auto';
    v.disablePictureInPicture = true;
    v.tabIndex = -1;
    v.setAttribute('aria-hidden', 'true');
    const poster = posterOf(c);
    if (poster) v.poster = poster;
    v.src = c.src;
    setTimeout(() => { c.wait = false; }, 5000);
    v.addEventListener('loadedmetadata', () => {
      if (isFinite(v.duration) && v.duration > 0) c.dur = v.duration;
      v.playbackRate = c.rate;
    }, { once: true });

    /* hold the clip inside its loop window: slow playback, cut back before the last act */
    v.addEventListener('timeupdate', () => {
      if (c.cutEnd >= 1) return;
      if (v.currentTime >= spanOf(c, v)) {
        v.currentTime = 0;
        if (c.playing && !c.held && v.paused) v.play().catch(() => {});
      }
    });
    v.addEventListener('loadeddata', () => c.stage.classList.add('is-live'), { once: true });
    const veil = c.stage.querySelector('.veil');
    c.stage.insertBefore(v, veil);
    c.video = v;
    start(c);
  }

  function detach(c: Chapter | undefined): void {
    if (!c || !c.video) return;
    c.stage.classList.remove('is-live');
    c.playing = false;
    c.video.pause();
    c.video.removeAttribute('src');
    c.video.load();
    c.video.remove();
    c.video = null;
  }

  const find = (t: Element) => chapters.find((c) => c.el === t);
  const near = new IntersectionObserver(
    (es) => es.forEach((e) => { if (e.isIntersecting) attach(find(e.target)); }),
    { rootMargin: NEAR },
  );
  const far = new IntersectionObserver(
    (es) => es.forEach((e) => { if (!e.isIntersecting) detach(find(e.target)); }),
    { rootMargin: FAR },
  );
  chapters.forEach((c) => { near.observe(c.el); far.observe(c.el); });

  function hold(c: Chapter, on: boolean): void {
    if (c.held === on) return;
    c.held = on;
    const v = c.video;
    if (!v || !c.playing) return;
    if (on) v.pause();
    else v.play().catch(() => {});
  }

  if (FINE) {
    chapters.forEach((c) => {
      c.stage.addEventListener('pointerover', (e) => {
        if ((e.target as Element).closest?.('.beat')) hold(c, true);
      });
      c.stage.addEventListener('pointerout', (e) => {
        const rel = (e as PointerEvent).relatedTarget as Element | null;
        if (!rel || !rel.closest?.('.beat')) hold(c, false);
      });
    });
  }

  function playhead(c: Chapter, dt: number): number {
    const v = c.video;
    if (c.playing && v && isFinite(v.duration) && v.duration > 0) {
      /* normalise against the loop window so every beat still lands inside it */
      const span = spanOf(c, v);
      if (c.cutEnd < 1 && v.currentTime >= span - 0.015) {
        v.currentTime = 0;
        c.clock = 0;
        return 0;
      }
      c.clock = clamp01(v.currentTime / span);
      return c.clock;
    }
    if (!c.held && !c.wait) c.clock = (c.clock + dt / Math.max(1, c.dur * c.cutEnd)) % 1;
    return c.clock;
  }

  function setState(el: HTMLElement, o: number, leaving: boolean): void {
    el.style.setProperty('--o', o.toFixed(3));
    el.style.setProperty('--o-inv', (1 - o).toFixed(3));
    el.style.setProperty('--dir', leaving ? '1' : '-1');
  }

  function drive(el: HTMLElement, local: number, i: number, n: number): void {
    const tIn = clamp01((local - i * STAGGER) / FADE);
    const tOut = clamp01((1 - local - (n - 1 - i) * STAGGER) / FADE);
    setState(el, ease(Math.min(tIn, tOut)), tOut < 1);
  }

  function applyBeats(c: Chapter, p: number): void {
    c.beats.forEach((b) => {
      const span = b.to - b.from;
      const local = span > 0 ? (p - b.from) / span : 0;
      const inside = local >= 0 && local < 1;
      b.el.style.setProperty('--local', clamp01(local).toFixed(3));
      if (!inside) {
        [...b.parts, ...b.lines, ...b.chips].forEach((el) => setState(el, 0, local > 1));
      } else {
        b.parts.forEach((el, i) => drive(el, local, i, b.parts.length));
        b.lines.forEach((el, i) => drive(el, local, i * 0.8, b.lines.length * 0.8));
        b.chips.forEach((el, i) => drive(el, local, 2 + i, 2 + b.chips.length));
      }
      b.el.classList.toggle('is-on', inside);
      b.el.classList.toggle('is-leaving', inside && 1 - local < FADE + STAGGER * 4);
    });
  }

  function setExit(c: Chapter): void {
    const top = c.el.getBoundingClientRect().top;
    c.el.style.setProperty('--exit', clamp01(-top / Math.max(1, innerHeight)).toFixed(3));
  }

  addEventListener('scroll', () => {
    document.documentElement.style.setProperty('--cue', '0');
  }, { once: true, passive: true });

  let raf = 0;
  let prev = 0;
  const inView = new Set<Element>();
  const run = new IntersectionObserver((es) => {
    es.forEach((e) => {
      if (e.isIntersecting) inView.add(e.target);
      else {
        inView.delete(e.target);
        const c = find(e.target);
        if (c) setExit(c);
      }
    });
    if (inView.size && !raf) {
      prev = 0;
      raf = requestAnimationFrame(tick);
    }
    if (!inView.size && raf) {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  });
  chapters.forEach((c) => run.observe(c.el));

  function tick(now: number): void {
    const dt = prev ? Math.min(0.1, (now - prev) / 1000) : 0;
    prev = now;
    for (const c of chapters) {
      setExit(c);
      applyBeats(c, playhead(c, dt));
    }
    raf = requestAnimationFrame(tick);
  }

  if (FINE) {
    const rootEl = document.documentElement;
    const MLERP = 0.08;
    let tx = 0;
    let ty = 0;
    let mx = 0;
    let my = 0;
    let mraf = 0;
    const settle = () => {
      mx += (tx - mx) * MLERP;
      my += (ty - my) * MLERP;
      rootEl.style.setProperty('--mx', mx.toFixed(3));
      rootEl.style.setProperty('--my', my.toFixed(3));
      mraf = Math.abs(tx - mx) + Math.abs(ty - my) > 0.002 ? requestAnimationFrame(settle) : 0;
    };
    const aim = (x: number, y: number) => {
      tx = x;
      ty = y;
      if (!mraf) mraf = requestAnimationFrame(settle);
    };
    addEventListener('pointermove', (e) => {
      aim((e.clientX / innerWidth) * 2 - 1, (e.clientY / innerHeight) * 2 - 1);
    }, { passive: true });
    document.addEventListener('mouseleave', () => aim(0, 0));
  }
}

export function initPanels(root: Document | HTMLElement = document): void {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const io = new IntersectionObserver((es) => es.forEach((e) => {
    if (!e.isIntersecting) return;
    e.target.classList.add('is-in');
    io.unobserve(e.target);
  }), { rootMargin: '0px 0px -12% 0px' });

  Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]')).forEach((el) => {
    const parent = el.parentElement;
    const siblings = parent
      ? Array.from(parent.children).filter((c) => c.hasAttribute('data-reveal'))
      : [el];
    el.style.setProperty('--i', String(siblings.indexOf(el)));
    io.observe(el);
  });

  const layers = Array.from(root.querySelectorAll<HTMLElement>('[data-parallax]'));
  let raf = 0;

  const paint = () => {
    raf = 0;
    const h = innerHeight;
    for (const el of layers) {
      const r = el.getBoundingClientRect();
      if (r.bottom < -h || r.top > 2 * h) continue;
      const centre = (r.top + r.height / 2 - h / 2) / h;
      let speed = parseFloat(el.dataset.parallax || '.32');
      if (el.classList.contains('panel-bg') && innerWidth < 780) speed = Math.min(speed, 0.22);
      el.style.setProperty('--py', `${(centre * -speed * h).toFixed(1)}px`);
    }
  };

  const soften = (el: HTMLElement) => {
    const media = el as HTMLImageElement & HTMLVideoElement;
    const nw = media.naturalWidth || media.videoWidth;
    const nh = media.naturalHeight || media.videoHeight;
    if (!nw || !nh) return;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const factor = Math.max(r.width / nw, r.height / nh);
    const px = factor <= 1.15 ? 0 : Math.min(1.6, Math.max(0.4, (factor - 1) * 1.2));
    el.style.setProperty('--soften', `${px.toFixed(2)}px`);
  };

  const SOFT = '.stage video, .plate, .panel-bg';
  const softenAll = () => document.querySelectorAll<HTMLElement>(SOFT).forEach(soften);
  document.addEventListener('load', (e) => {
    const t = e.target as HTMLElement | null;
    if (t && t.matches && t.matches(SOFT)) soften(t);
  }, true);
  document.addEventListener('loadedmetadata', (e) => {
    const t = e.target as HTMLElement | null;
    if (t && t.matches && t.matches(SOFT)) soften(t);
  }, true);
  addEventListener('resize', softenAll);
  setTimeout(softenAll, 600);

  const manifestos = Array.from(root.querySelectorAll<HTMLElement>('[data-words]')).map((el) => {
    const wrap = (node: Node): void => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const frag = document.createDocumentFragment();
          (child.textContent || '').split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part));
              return;
            }
            const w = document.createElement('span');
            w.className = 'w';
            w.textContent = part;
            frag.appendChild(w);
          });
          child.parentNode?.replaceChild(frag, child);
        } else if (child.nodeType === 1) {
          wrap(child);
        }
      });
    };
    wrap(el);
    return { section: el.closest('section') || el, words: Array.from(el.querySelectorAll<HTMLElement>('.w')) };
  });

  const light = () => {
    const h = innerHeight;
    for (const m of manifestos) {
      const r = m.section.getBoundingClientRect();
      if (r.bottom < 0 || r.top > h) continue;
      const p = Math.min(1, Math.max(0, (h * 0.8 - r.top) / (r.height + h * 0.45)));
      const n = m.words.length;
      m.words.forEach((w, i) => w.style.setProperty('--lit', p * (n + 1) > i + 1 ? '1' : '0'));
    }
  };

  addEventListener('scroll', () => requestAnimationFrame(light), { passive: true });
  light();

  addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(paint); }, { passive: true });
  addEventListener('resize', paint);
  paint();
}